import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSponsorManagement } from './useSponsorManagement';

const SponsorManagementPage: React.FC = () => {
  console.log('[SponsorManagementPage] Component rendering');

  const {
    loading,
    error,
    users,
    sponsorGroups,
    selectedUserIds,
    canUpdateSponsor,
    confirmingUsers,
    disablingUser,
    updatingSponsor,
    toggleUserSelection,
    selectAll,
    clearSelection,
    confirmSelectedUsers,
    disableUser,
    updateUserSponsor,
  } = useSponsorManagement();

  if (loading) {
    console.log('[SponsorManagementPage] Showing loading state');
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error) {
    console.error('[SponsorManagementPage] Error state:', error);
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (users.length === 0) {
    console.log('[SponsorManagementPage] No users found');
    return (
      <div className="p-6">
        <Alert>
          <AlertTitle>No users found</AlertTitle>
          <AlertDescription>There are no sponsored users to display.</AlertDescription>
        </Alert>
      </div>
    );
  }

  console.log('[SponsorManagementPage] Rendering main view', {
    userCount: users.length,
    groupCount: sponsorGroups.length,
    selectedCount: selectedUserIds.size
  });

  const selectedCount = selectedUserIds.size;
  const totalUsers = users.filter((u) => !u.disabled).length;
  const allSelected = totalUsers > 0 && selectedCount === totalUsers;
  const busy = confirmingUsers || disablingUser || updatingSponsor;

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <div className="border-b bg-white p-4">
        <h1 className="mb-4 text-2xl font-bold">Sponsor Management</h1>

        {/* Action Bar */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="select-all"
              checked={allSelected}
              onCheckedChange={(checked) => selectAll(checked === true)}
              disabled={totalUsers === 0}
            />
            <label htmlFor="select-all" className="text-sm">
              Select all ({selectedCount} selected)
            </label>
          </div>

          <Button onClick={confirmSelectedUsers} disabled={selectedCount === 0 || busy} size="sm">
            Confirm Selected
          </Button>

          {selectedCount > 0 && (
            <Button onClick={clearSelection} variant="outline" size="sm" disabled={busy}>
              Clear Selection
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-6">
          {sponsorGroups.map((group) => {
            const groupTitle =
              !group.sponsorDisplayName || group.sponsorDisplayName === group.sponsorName
                ? group.sponsorName
                : `${group.sponsorName} - ${group.sponsorDisplayName}`;

            return (
              <div key={group.sponsorName} className="rounded-lg border bg-white p-4">
                <h2 className="mb-4 text-lg font-semibold">{groupTitle}</h2>

              <div className="space-y-2">
                {group.users.map((user) => {
                  const isSelected = selectedUserIds.has(user.id);
                  const displayName = user.realname || user.username;

                  return (
                    <div
                      key={user.id}
                      className="flex items-center gap-4 rounded border p-3 hover:bg-gray-50"
                    >
                      <Checkbox
                        aria-label={`Select ${displayName}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleUserSelection(user.id)}
                        disabled={user.disabled}
                      />

                      <div className="flex-1">
                        <div className="font-medium">{displayName}</div>
                        <div className="text-sm text-gray-600">
                          @{user.username} • {user.email}
                        </div>
                        {user.last_confirmation && (
                          <div className="text-xs text-gray-500">
                            Last confirmed: {new Date(user.last_confirmation).toLocaleDateString()}
                            {user.days_since_confirmation !== null &&
                              ` (${user.days_since_confirmation} days ago)`}
                          </div>
                        )}
                        {!user.last_confirmation && (
                          <div className="text-xs text-red-600">Never confirmed</div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {canUpdateSponsor && (
                          <Select
                            value={user.sponsor ?? 'UNASSIGNED'}
                            onValueChange={(value) => {
                              if (value !== 'UNASSIGNED' && value !== user.sponsor) {
                                updateUserSponsor(user.id, value);
                              }
                            }}
                            disabled={updatingSponsor}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="UNASSIGNED" disabled>
                                Select sponsor...
                              </SelectItem>
                              <SelectItem value="ODR MANAGER">ODR MANAGER</SelectItem>
                            </SelectContent>
                          </Select>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => disableUser(user.username)}
                          disabled={user.disabled || disablingUser}
                        >
                          {user.disabled ? 'Disabled' : 'Disable'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SponsorManagementPage;
